import { ByteArrayInput, isDefined, Option, tryCatch, UUID } from "@opendaw/lib-std";
import { BoxGraph } from "@opendaw/lib-box";
import { StorageWalk } from "@opendaw/lib-fusion";
import { AudioFileBox, BoxIO, SoundfontFileBox, WclapDeviceBox, WclapInstrumentBox } from "@opendaw/studio-boxes";
import { ProjectSkeleton } from "../project/ProjectSkeleton";
import { PresetHeader } from "../preset/PresetHeader";
export var StorageScan;
(function (StorageScan) {
    const WclapScheme = "opfs:";
    const Bookkeeping = new Set([
        "projects/v1/trash.json", "templates/v1/trash.json", "samples/v2/trash.json", "soundfont/trash.json",
        "scripts/v1/trash.json", "wclap/tombstones.json", "presets/user/index.json", "presets/user/trash.json"
    ]);
    const Rules = [
        { prefix: "projects/v1/", depth: 1, kind: "project" },
        { prefix: "templates/v1/", depth: 1, kind: "template" },
        { prefix: "samples/v2/", depth: 1, kind: "sample" },
        { prefix: "soundfont/", depth: 1, kind: "soundfont" },
        { prefix: "wclap/", depth: 1, kind: "wclap" },
        { prefix: "scripts/v1/", depth: 1, kind: "script" },
        { prefix: "inference/models/", depth: 2, kind: "model" },
        { prefix: "tone3000/", depth: 1, kind: "nam-pack" }
    ];
    const PresetFolder = "presets/user/";
    const isAssetKind = (kind) => kind === "sample" || kind === "soundfont" || kind === "wclap";
    const classify = (files) => {
        const entries = new Map();
        const other = [];
        const bookkeeping = StorageWalk.totalSize(files.filter(({ path }) => Bookkeeping.has(path)));
        for (const file of files) {
            if (Bookkeeping.has(file.path)) {
                continue;
            }
            const optEntry = entryOf(file.path);
            if (optEntry.isEmpty()) {
                other.push(file);
                continue;
            }
            const entry = optEntry.unwrap();
            const key = `${entry.kind}:${entry.id}`;
            const existing = entries.get(key);
            if (isDefined(existing)) {
                existing.size += file.size;
            }
            else {
                entries.set(key, { ...entry, size: file.size });
            }
        }
        return { entries: Array.from(entries.values()), other, bookkeeping };
    };
    const entryOf = (path) => {
        if (path.startsWith(PresetFolder) && path.endsWith(".odp") && path.indexOf("/", PresetFolder.length) === -1) {
            return Option.wrap({ kind: "preset", id: path.substring(PresetFolder.length, path.length - 4), path });
        }
        for (const { prefix, depth, kind } of Rules) {
            if (!path.startsWith(prefix)) {
                continue;
            }
            const segments = path.substring(prefix.length).split("/");
            if (segments.length <= depth) {
                return Option.None;
            }
            const id = segments.slice(0, depth).join("/");
            return Option.wrap({ kind, id, path: `${prefix}${id}` });
        }
        return Option.None;
    };
    const readJson = async (read, path) => {
        const bytes = await read(path).then(Option.wrap, () => Option.None);
        if (bytes.isEmpty()) {
            return Option.None;
        }
        const parsed = tryCatch(() => JSON.parse(new TextDecoder().decode(bytes.unwrap())));
        return parsed.status === "success" && typeof parsed.value === "object" && parsed.value !== null
            ? Option.wrap(parsed.value) : Option.None;
    };
    const stringOf = (record, key) => record.flatMap(value => typeof value[key] === "string" ? Option.wrap(value[key]) : Option.None);
    const exactBuffer = (bytes) => bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength);
    const decodeProject = (bytes) => ProjectSkeleton.decode(exactBuffer(bytes)).boxGraph;
    const decodePreset = (bytes) => {
        const buffer = exactBuffer(bytes);
        const header = new ByteArrayInput(buffer);
        if (header.readInt() !== PresetHeader.MAGIC_HEADER_OPEN) {
            throw new Error("Not a preset");
        }
        if (header.readInt() !== PresetHeader.FORMAT_VERSION) {
            throw new Error("Unknown preset version");
        }
        const graph = new BoxGraph(Option.wrap(BoxIO.create));
        graph.fromArrayBuffer(buffer.slice(8), false);
        return graph;
    };
    StorageScan.referencesOf = (graph) => {
        const refs = new Map();
        const clapIds = new Map();
        for (const box of graph.boxes()) {
            if (box instanceof AudioFileBox) {
                const id = UUID.toString(box.address.uuid);
                refs.set(`sample:${id}`, { kind: "sample", id });
            }
            else if (box instanceof SoundfontFileBox) {
                const id = UUID.toString(box.address.uuid);
                refs.set(`soundfont:${id}`, { kind: "soundfont", id });
            }
            else if (box instanceof WclapDeviceBox || box instanceof WclapInstrumentBox) {
                const url = box.url.getValue();
                if (!url.startsWith(WclapScheme)) {
                    continue;
                }
                const id = url.substring(WclapScheme.length);
                refs.set(`wclap:${id}`, { kind: "wclap", id });
                const clapId = box.clapId.getValue();
                const list = clapIds.get(id) ?? [];
                if (clapId.length > 0 && !list.includes(clapId)) {
                    list.push(clapId);
                }
                clapIds.set(id, list);
            }
        }
        return { refs: Array.from(refs.values()), clapIds };
    };
    StorageScan.scan = async (walk, read, progress) => {
        const { entries, other, bookkeeping } = classify(walk.files);
        const failures = [...walk.failures];
        const presetIndex = await read(`${PresetFolder}index.json`)
            .then(bytes => tryCatch(() => JSON.parse(new TextDecoder().decode(bytes))), () => null);
        const presetName = new Map();
        if (isDefined(presetIndex) && presetIndex.status === "success" && Array.isArray(presetIndex.value)) {
            for (const entry of presetIndex.value) {
                if (typeof entry.uuid === "string" && typeof entry.name === "string") {
                    presetName.set(entry.uuid, entry.name);
                }
            }
        }
        const decodable = entries.filter(({ kind }) => kind === "project" || kind === "template" || kind === "preset");
        const usedBy = new Map();
        const clapIds = new Map();
        const references = new Map();
        for (const [index, entry] of decodable.entries()) {
            const file = entry.kind === "preset" ? entry.path : `${entry.path}/project.od`;
            const bytes = await read(file).then(Option.wrap, () => Option.None);
            const decoded = bytes.isEmpty() ? null : tryCatch(() => entry.kind === "preset" ? decodePreset(bytes.unwrap()) : decodeProject(bytes.unwrap()));
            if (!isDefined(decoded) || decoded.status === "failure") {
                failures.push(file);
            }
            else {
                const { refs, clapIds: ids } = StorageScan.referencesOf(decoded.value);
                references.set(`${entry.kind}:${entry.id}`, refs);
                for (const ref of refs) {
                    const key = `${ref.kind}:${ref.id}`;
                    const list = usedBy.get(key) ?? [];
                    list.push({ kind: entry.kind, id: entry.id });
                    usedBy.set(key, list);
                }
                ids.forEach((values, id) => {
                    const list = clapIds.get(id) ?? [];
                    values.forEach(value => { if (!list.includes(value)) {
                        list.push(value);
                    } });
                    clapIds.set(id, list);
                });
            }
            progress((index + 1) / decodable.length);
        }
        const stored = new Set(entries.filter(({ kind }) => isAssetKind(kind)).map(({ kind, id }) => `${kind}:${id}`));
        const assets = [];
        const items = [];
        for (const entry of entries) {
            const key = `${entry.kind}:${entry.id}`;
            if (isAssetKind(entry.kind)) {
                const meta = entry.kind === "wclap" ? Option.None : await readJson(read, `${entry.path}/meta.json`);
                const ids = clapIds.get(entry.id) ?? [];
                assets.push({
                    kind: entry.kind, id: entry.id, path: entry.path, size: entry.size,
                    name: stringOf(meta, "name").unwrapOrElse(ids.at(0) ?? entry.id),
                    origin: stringOf(meta, "origin").unwrapOrNull(), clapIds: ids, usedBy: usedBy.get(key) ?? []
                });
            }
            else {
                const uses = references.get(key) ?? [];
                items.push({
                    kind: entry.kind, id: entry.id, path: entry.path, size: entry.size,
                    name: await itemName(read, entry, presetName),
                    uses: uses.filter(ref => stored.has(`${ref.kind}:${ref.id}`)),
                    notStored: uses.filter(ref => !stored.has(`${ref.kind}:${ref.id}`))
                });
            }
        }
        progress(1.0);
        return { total: StorageWalk.totalSize(walk.files), bookkeeping, assets, items, other, failures };
    };
    const itemName = async (read, { kind, id, path }, presetName) => {
        switch (kind) {
            case "preset":
                return presetName.get(id) ?? id;
            case "model":
                return id.replace("/", " ");
            case "nam-pack":
                return stringOf(await readJson(read, `${path}/pack.json`), "title").unwrapOrElse(id);
            default:
                return stringOf(await readJson(read, `${path}/meta.json`), "name").unwrapOrElse(id);
        }
    };
})(StorageScan || (StorageScan = {}));
